import { supabase } from '@/integrations/supabase/client';
import { ContextualSearchEngine } from './contextualSearch';
import { HybridSearchResult, FuzzySearchResult, SearchContext, ContextualIcon } from '@/types/search';

interface HybridSearchCache {
  query: string;
  results: HybridSearchResult[];
  timestamp: number;
}

export class HybridSearchEngine {
  private static cache: Map<string, HybridSearchCache> = new Map();
  private static CACHE_DURATION = 30000; // 30 secondes

  /**
   * Recherche hybride combinant fuzzy search Supabase + contextes IA
   */
  static async search(
    query: string,
    options: {
      similarityThreshold?: number;
      resultLimit?: number;
    } = {}
  ): Promise<HybridSearchResult[]> {
    const { similarityThreshold = 0.3, resultLimit = 20 } = options;

    // Vérifier le cache
    const cached = this.getFromCache(query);
    if (cached) {
      return cached;
    }

    try {
      // Phase 1: Recherche floue Supabase
      const { data: fuzzyResults, error } = await supabase.rpc('search_members_fuzzy', {
        query_text: query,
        similarity_threshold: similarityThreshold,
        result_limit: resultLimit,
      });

      if (error) throw error;

      // Phase 2: Enrichissement contextuel
      const searchContext = ContextualSearchEngine.parseSearchIntent(query);
      const contextualIcons = ContextualSearchEngine.generateContextualIcons(query);

      // Phase 3: Fusion des résultats avec scoring hybride
      const hybridResults = this.enrichResults(
        fuzzyResults || [],
        query,
        searchContext,
        contextualIcons
      );

      // Enregistrer la recherche pour analytics
      await this.recordSearch(query);

      // Mettre en cache
      this.addToCache(query, hybridResults);

      return hybridResults;
    } catch (error) {
      console.error('Erreur recherche hybride:', error);
      return [];
    }
  }

  /**
   * Enrichit les résultats fuzzy avec les données contextuelles
   */
  private static enrichResults(
    fuzzyResults: FuzzySearchResult[],
    query: string,
    searchContext: SearchContext | null,
    contextualIcons: ContextualIcon[]
  ): HybridSearchResult[] {
    return fuzzyResults.map((result) => {
      // Calculer le score hybride
      const fuzzyScore = result.relevance_score;
      const contextScore = this.calculateContextScore(result, query, contextualIcons);
      const popularityScore = this.calculatePopularityScore(result);

      // Formule de scoring hybride
      const hybridScore =
        fuzzyScore * 0.6 + // 60% basé sur la pertinence fuzzy
        contextScore * 0.3 + // 30% basé sur le contexte
        popularityScore * 0.1; // 10% basé sur la popularité

      // Générer les icônes contextuelles spécifiques au membre
      const memberContextualIcons = this.generateMemberContextualIcons(
        result,
        contextualIcons
      );

      return {
        ...result,
        contextualIcons: memberContextualIcons,
        smartTags: [], // Peut être enrichi plus tard avec SmartTagGenerator
        hybridScore,
        searchIntent: searchContext || undefined,
      };
    }).sort((a, b) => b.hybridScore - a.hybridScore);
  }

  /**
   * Calcule le score contextuel basé sur les icônes
   */
  private static calculateContextScore(
    result: FuzzySearchResult,
    query: string,
    contextualIcons: ContextualIcon[]
  ): number {
    if (contextualIcons.length === 0) return 0.5; // Score neutre

    const resultText = [
      result.member_name,
      result.member_company,
      result.member_role,
    ].join(' ').toLowerCase();

    let totalScore = 0;
    let matchCount = 0;

    for (const icon of contextualIcons) {
      if (resultText.includes(icon.keyword)) {
        totalScore += icon.matchScore / 100;
        matchCount++;
      }
    }

    return matchCount > 0 ? totalScore / matchCount : 0.3;
  }

  /**
   * Calcule le score de popularité (mock - à implémenter avec vraies données)
   */
  private static calculatePopularityScore(result: FuzzySearchResult): number {
    // Score neutre en attendant de vraies métriques
    return 0.5;
  }

  /**
   * Génère les icônes contextuelles spécifiques au membre
   */
  private static generateMemberContextualIcons(
    result: FuzzySearchResult,
    globalIcons: ContextualIcon[]
  ): ContextualIcon[] {
    const memberText = [
      result.member_name,
      result.member_company,
      result.member_role,
    ].join(' ').toLowerCase();

    return globalIcons
      .filter((icon) => memberText.includes(icon.keyword))
      .slice(0, 3);
  }

  /**
   * Gestion du cache
   */
  private static getFromCache(query: string): HybridSearchResult[] | null {
    const cached = this.cache.get(query);
    if (!cached) return null;

    const isExpired = Date.now() - cached.timestamp > this.CACHE_DURATION;
    if (isExpired) {
      this.cache.delete(query);
      return null;
    }

    return cached.results;
  }

  private static addToCache(query: string, results: HybridSearchResult[]): void {
    this.cache.set(query, {
      query,
      results,
      timestamp: Date.now(),
    });

    // Nettoyer le cache si trop grand
    if (this.cache.size > 50) {
      const firstKey = this.cache.keys().next().value;
      if (firstKey) this.cache.delete(firstKey);
    }
  }

  /**
   * Enregistre la recherche pour analytics
   */
  private static async recordSearch(query: string): Promise<void> {
    try {
      await supabase.rpc('record_search', {
        search_term: query,
        search_category: 'hybrid_member_search',
      });
    } catch (error) {
      // Silently fail - analytics shouldn't block search
      console.error('Erreur enregistrement recherche:', error);
    }
  }

  /**
   * Nettoie le cache
   */
  static clearCache(): void {
    this.cache.clear();
  }
}
